import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fingerprint, KeyRound, ShieldCheck, Smartphone } from "lucide-react";
import {
  api,
  ApiError,
  type Me,
  type MfaEnrolment,
  type MfaRecoveryCodes,
  type MfaStatus,
  type Passkey,
  type PasskeyChallenge,
} from "@/lib/api";
import {
  createPasskey,
  PasskeyError,
  passkeyVerify,
  supportsPasskeys,
} from "@/lib/webauthn";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  ErrorNote,
  Field,
  Input,
  Notice,
  PageHeader,
  Spinner,
  Table,
  Td,
  Th,
} from "@/components/ui";
import { useAuth } from "@/app/auth";
import { dateTime, relative } from "@/lib/format";

// Der zweite Faktor.
//
// Zwei Bildschirme, weil es zwei Situationen gibt. /mfa ist die Schranke: die
// Anmeldung ist halb fertig, das Passwort war richtig, und ohne Code geht es
// nicht weiter — entweder wird der Faktor jetzt eingerichtet oder er wird
// abgefragt. /security ist die Selbstverwaltung danach: wie viele
// Wiederherstellungscodes noch übrig sind und wie man neue bekommt.

/** Ein sechsstelliger Code oder ein Wiederherstellungscode. */
function CodeField({
  value,
  onChange,
  label = "Code aus der App",
  hint,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  hint?: string;
  autoFocus?: boolean;
}) {
  return (
    <Field label={label} hint={hint}>
      <Input
        // inputMode numeric holt auf dem Telefon die Zifferntastatur; type=text,
        // weil ein Wiederherstellungscode Buchstaben enthält und type=number
        // führende Nullen frisst.
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        required
        className="font-mono tracking-[0.2em]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

/** Die Codes, die genau einmal zu sehen sind. */
function RecoveryCodes({ codes }: { codes: string[] }) {
  return (
    <div className="flex flex-col gap-2">
      <Notice tone="warning">
        Diese zehn Codes ersetzen die App, wenn das Telefon weg ist. Sie werden
        nur jetzt angezeigt — danach steht bei uns nur noch ihre Prüfsumme.
        Ausdrucken oder in den Passwortmanager legen.
      </Notice>
      <ul className="grid grid-cols-2 gap-1 rounded-md border surface p-3 font-mono text-sm">
        {codes.map((c) => (
          <li key={c} className="tabular-nums">
            {c}
          </li>
        ))}
      </ul>
      <div>
        <Button
          size="sm"
          onClick={() => void navigator.clipboard?.writeText(codes.join("\n"))}
        >
          In die Zwischenablage
        </Button>
      </div>
    </div>
  );
}

/**
 * Die Schranke nach der Passwortanmeldung.
 *
 * Sie führt entweder durch die Einrichtung oder fragt den Code ab; welches von
 * beidem, sagt `mfaEnrolled`. Beide Wege enden damit, dass die Sitzung
 * vollständig wird und der Router wieder ganz normal weiterleitet.
 */
export function MfaGatePage() {
  const { me, loading, signOut, setMe } = useAuth();
  const navigate = useNavigate();

  if (loading) return <Spinner label="Anmeldung wird geprüft" />;
  if (!me) return <Navigate to="/signin" replace />;
  if (!me.mfaPending) return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md p-6">
        <div className="mb-5 flex items-center gap-3">
          <span
            className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-600 text-white shadow-xs"
            aria-hidden
          >
            <ShieldCheck className="size-5" />
          </span>
          <div className="leading-tight">
            <h1 className="text-lg font-semibold tracking-tight">
              {me.mfaEnrolled ? "Bestätigung" : "Zwei-Faktor einrichten"}
            </h1>
            <p className="text-sm muted">{me.email}</p>
          </div>
        </div>

        {me.mfaEnrolled ? (
          <Challenge onDone={setMe} />
        ) : (
          <Enrolment onDone={setMe} />
        )}

        <div className="mt-5 border-t pt-3" style={{ borderColor: "var(--border)" }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              await signOut();
              navigate("/signin", { replace: true });
            }}
          >
            Abbrechen und abmelden
          </Button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Der Code wird abgefragt: die App oder ein Wiederherstellungscode — oder,
 * wer einen registriert hat, der Passkey an seiner Stelle.
 */
function Challenge({ onDone }: { onDone: (me: Me) => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  // GET /api/admin/mfa ist auch halb angemeldet erreichbar; es sagt, ob es
  // überhaupt einen Passkey gibt, den man anbieten könnte.
  const { data: status } = useQuery({
    queryKey: ["mfa"],
    queryFn: () => api<MfaStatus>("/api/admin/mfa"),
  });
  const offerPasskey =
    supportsPasskeys() && !!status?.passkeysAvailable && (status?.passkeys ?? 0) > 0;

  async function withPasskey() {
    setError(null);
    setPasskeyBusy(true);
    try {
      onDone(await passkeyVerify());
    } catch (err) {
      if (err instanceof PasskeyError && err.cancelled) return;
      setError(
        err instanceof ApiError || err instanceof PasskeyError
          ? err.message
          : "Der Passkey konnte nicht geprüft werden.",
      );
    } finally {
      setPasskeyBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const me = await api<Me>("/api/admin/mfa/verify", {
        method: "POST",
        body: { code },
      });
      onDone(me);
    } catch (err) {
      setCode("");
      setError(
        err instanceof ApiError ? err.detail : "Der Code konnte nicht geprüft werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit}>
      <p className="text-sm muted">
        Die Verwaltung fragt bei jeder Anmeldung nach dem Code aus Ihrer
        Authenticator-App.
      </p>
      <CodeField
        value={code}
        onChange={setCode}
        autoFocus
        hint="Kein Telefon zur Hand? Einer der Wiederherstellungscodes geht auch."
      />
      {error && <ErrorNote>{error}</ErrorNote>}
      <Button
        type="submit"
        variant="primary"
        disabled={busy || passkeyBusy || code.length < 6}
      >
        {busy ? "wird geprüft…" : "Weiter"}
      </Button>
      {offerPasskey && (
        <Button
          type="button"
          disabled={busy || passkeyBusy}
          onClick={() => void withPasskey()}
        >
          <Fingerprint className="size-4" aria-hidden />
          {passkeyBusy ? "warte auf den Passkey…" : "Mit Passkey bestätigen"}
        </Button>
      )}
    </form>
  );
}

/**
 * Die Einrichtung: QR-Code scannen, einen erzeugten Code zurückgeben, die
 * Wiederherstellungscodes mitnehmen.
 *
 * Das Geheimnis wird angelegt, sobald der Bildschirm geöffnet wird, und ist
 * erst gültig, wenn ein Code daraus zurückgekommen ist. Wer hier abbricht,
 * lässt nichts Brauchbares zurück.
 */
function Enrolment({ onDone }: { onDone: (me: Me) => void }) {
  const [enrolment, setEnrolment] = useState<MfaEnrolment | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    void api<MfaEnrolment>("/api/admin/mfa/totp", { method: "POST" })
      .then((e) => !cancelled && setEnrolment(e))
      .catch(
        (err: unknown) =>
          !cancelled &&
          setError(
            err instanceof ApiError
              ? err.detail
              : "Die Einrichtung konnte nicht gestartet werden.",
          ),
      );
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api<MfaRecoveryCodes>("/api/admin/mfa/totp/confirm", {
        method: "POST",
        body: { code },
      });
      setCodes(res.codes);
    } catch (err) {
      setCode("");
      setError(
        err instanceof ApiError ? err.detail : "Der Code konnte nicht geprüft werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (codes) {
    return (
      <div className="flex flex-col gap-4">
        <Notice tone="success">
          Der zweite Faktor ist eingerichtet. Ab jetzt fragt die Verwaltung bei
          jeder Anmeldung danach.
        </Notice>
        <RecoveryCodes codes={codes} />
        <Button
          variant="primary"
          onClick={async () => {
            // Die Sitzung ist mit dem Bestätigen vollständig geworden; wir
            // holen uns den neuen Stand, statt ihn zu erraten.
            const me = await api<Me>("/api/admin/me");
            onDone(me);
            navigate("/", { replace: true });
          }}
        >
          Ich habe die Codes gesichert — weiter
        </Button>
      </div>
    );
  }

  if (error && !enrolment) return <ErrorNote>{error}</ErrorNote>;
  if (!enrolment) return <Spinner label="Geheimnis wird erzeugt" />;

  return (
    <form className="flex flex-col gap-4" onSubmit={submit}>
      <ol className="flex flex-col gap-1 text-sm muted">
        <li>
          1. Eine Authenticator-App öffnen (Google Authenticator, 1Password,
          Microsoft Authenticator …).
        </li>
        <li>2. Den Code abscannen.</li>
        <li>3. Die sechs Ziffern hier eintragen.</li>
      </ol>

      <div className="flex flex-col items-center gap-2">
        {/* Das Bild ist das Geheimnis. Es kommt aus einer eigenen Anfrage und
            nicht als Datenblock im JSON, damit es nie durch Skript läuft. */}
        <img
          src={enrolment.qrUrl}
          alt="QR-Code für die Authenticator-App"
          width={205}
          height={205}
          className="rounded-md border"
          style={{ borderColor: "var(--border)" }}
        />
        <details className="w-full text-center text-xs muted">
          <summary className="cursor-pointer">
            <Smartphone className="mr-1 inline size-3" aria-hidden />
            Kamera streikt? Schlüssel zum Abtippen
          </summary>
          <code className="mt-1 block break-all rounded-md border surface p-2 font-mono text-[0.8125rem] tracking-wider">
            {enrolment.secret}
          </code>
        </details>
      </div>

      <CodeField value={code} onChange={setCode} autoFocus />
      {error && <ErrorNote>{error}</ErrorNote>}
      <Button type="submit" variant="primary" disabled={busy || code.length < 6}>
        {busy ? "wird geprüft…" : "Einrichten"}
      </Button>
    </form>
  );
}

/**
 * Die Seite für danach: Zustand des eigenen Faktors und neue
 * Wiederherstellungscodes.
 */
export function SecurityPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["mfa"],
    queryFn: () => api<MfaStatus>("/api/admin/mfa"),
  });

  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const regenerate = useMutation({
    mutationFn: () =>
      api<MfaRecoveryCodes>("/api/admin/mfa/recovery-codes", {
        method: "POST",
        body: { code },
      }),
    onSuccess: (res) => {
      setCodes(res.codes);
      setCode("");
      setError(null);
      void qc.invalidateQueries({ queryKey: ["mfa"] });
    },
    onError: (err: unknown) => {
      setCode("");
      setError(
        err instanceof ApiError ? err.detail : "Die Codes konnten nicht erneuert werden.",
      );
    },
  });

  if (isLoading) return <Spinner />;
  if (!data) return <ErrorNote>Der Zustand konnte nicht geladen werden.</ErrorNote>;

  const low = data.recoveryCodesLeft <= 3;

  return (
    <>
      <PageHeader
        title="Sicherheit"
        description="Der zweite Faktor dieses Kontos und die Passkeys daneben."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <div className="mb-3 flex items-center gap-2">
            <ShieldCheck className="size-4 muted" aria-hidden />
            <h2 className="text-sm font-semibold">Zweiter Faktor</h2>
            <Badge tone={data.enrolled ? "success" : "warning"}>
              {data.enrolled ? "Authenticator-App" : "nicht eingerichtet"}
            </Badge>
            {data.passkeys > 0 && (
              <Badge tone="info">
                {data.passkeys === 1 ? "1 Passkey" : `${data.passkeys} Passkeys`}
              </Badge>
            )}
          </div>
          <p className="text-sm muted">
            Jede Rolle, die in die Verwaltung darf, braucht einen zweiten Faktor.
            Das ist nicht abschaltbar — wer die Mobilboxen anderer Leute
            verwaltet, soll nicht nur ein Passwort dazwischen haben.
          </p>
          {data.enrolled && (
            <p className="mt-3 text-sm">
              Wiederherstellungscodes übrig:{" "}
              <Badge tone={low ? "warning" : "neutral"}>
                {data.recoveryCodesLeft}
              </Badge>
            </p>
          )}
          <p className="mt-3 text-xs muted">
            Telefon verloren und keine Codes mehr? Ein globaler Administrator
            setzt den Faktor auf der Benutzerseite zurück; auf dem Server tut es{" "}
            <code className="font-mono">mbmctl mfa-reset -email …</code>.
          </p>
        </Card>

        <Card className="p-4">
          <div className="mb-3 flex items-center gap-2">
            <KeyRound className="size-4 muted" aria-hidden />
            <h2 className="text-sm font-semibold">Wiederherstellungscodes erneuern</h2>
          </div>

          {codes ? (
            <RecoveryCodes codes={codes} />
          ) : (
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                regenerate.mutate();
              }}
            >
              <p className="text-sm muted">
                Zehn neue Codes; die alten gelten danach nicht mehr. Weil die
                neuen Codes an der App vorbeiführen, ist dafür ein aktueller Code
                aus der App nötig.
              </p>
              <CodeField value={code} onChange={setCode} />
              {error && <ErrorNote>{error}</ErrorNote>}
              <div>
                <Button
                  type="submit"
                  disabled={!data.enrolled || code.length < 6 || regenerate.isPending}
                >
                  {regenerate.isPending ? "wird erneuert…" : "Neue Codes erzeugen"}
                </Button>
              </div>
            </form>
          )}
        </Card>

        <div className="lg:col-span-2">
          <PasskeysCard status={data} />
        </div>
      </div>
    </>
  );
}

/**
 * Die Passkeys: Liste, Hinzufügen, Umbenennen, Entfernen.
 *
 * Sie kommen zur App dazu, nicht an ihre Stelle — deshalb setzt das
 * Hinzufügen eine eingerichtete App voraus. Mehr nicht: Name eintippen, und
 * der Browser zeigt seinen eigenen Dialog (Touch ID, Sicherheitsschlüssel
 * oder QR-Code fürs Telefon).
 */
function PasskeysCard({ status }: { status: MfaStatus }) {
  const qc = useQueryClient();
  const { data: keys, isLoading } = useQuery({
    queryKey: ["passkeys"],
    queryFn: () => api<Passkey[]>("/api/admin/passkeys"),
    enabled: status.passkeysAvailable,
  });

  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Passkey | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["passkeys"] });
    void qc.invalidateQueries({ queryKey: ["mfa"] });
  };

  const remove = useMutation({
    mutationFn: (id: number) => api<void>(`/api/admin/passkeys/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });

  const rename = useMutation({
    mutationFn: (vars: { id: number; name: string }) =>
      api<void>(`/api/admin/passkeys/${vars.id}`, {
        method: "PUT",
        body: { name: vars.name },
      }),
    onSuccess: refresh,
  });

  // Beide Anfragen und der Browseraufruf gehören in denselben Klick: Safari
  // erlaubt navigator.credentials nur in einer Nutzergeste.
  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setAdded(null);
    setBusy(true);
    try {
      const challenge = await api<PasskeyChallenge<unknown>>("/api/admin/passkeys/options", {
        method: "POST",
        body: {},
      });
      const credential = await createPasskey(challenge.options);
      const key = await api<Passkey>("/api/admin/passkeys", {
        method: "POST",
        body: { token: challenge.token, name, credential },
      });
      setAdded(key.name);
      setName("");
      refresh();
    } catch (err) {
      if (err instanceof PasskeyError && err.cancelled) return;
      setError(
        err instanceof ApiError || err instanceof PasskeyError
          ? err.message
          : "Der Passkey konnte nicht hinzugefügt werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <Fingerprint className="size-4 muted" aria-hidden />
        <h2 className="text-sm font-semibold">Passkeys</h2>
      </div>
      <p className="mb-4 text-sm muted">
        Ein Passkey meldet Sie ohne Passwort und ohne Code an — Touch ID, Windows
        Hello, ein Sicherheitsschlüssel oder der Passwortmanager. An der
        Schranke nach der Passwortanmeldung ersetzt er den Code aus der App.
        Die App bleibt daneben bestehen; ein verlorener Passkey kostet nur den
        Passkey.
      </p>

      {!status.passkeysAvailable ? (
        <Notice tone="info">
          Auf dieser Installation sind Passkeys nicht eingerichtet: der Server
          kennt seine eigene Adresse nicht (<code className="font-mono">MBM_ADMIN_BASE_URL</code>).
        </Notice>
      ) : !supportsPasskeys() ? (
        <Notice tone="info">
          Dieser Browser unterstützt keine Passkeys. In einem aktuellen Chrome,
          Safari, Edge oder Firefox lassen sie sich hier einrichten.
        </Notice>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            {isLoading ? (
              <Spinner />
            ) : !keys || keys.length === 0 ? (
              <p className="text-sm muted">Noch kein Passkey registriert.</p>
            ) : (
              <Table dense>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Art</Th>
                    <Th>Angelegt</Th>
                    <Th>Zuletzt benutzt</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <PasskeyRow
                      key={k.id}
                      passkey={k}
                      onRename={(n) => rename.mutate({ id: k.id, name: n })}
                      onRemove={() => setRemoving(k)}
                    />
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          <form className="flex flex-col gap-3" onSubmit={add}>
            <h3 className="text-sm font-semibold">Passkey hinzufügen</h3>
            {!status.enrolled && (
              <Notice tone="warning">
                Zuerst die Authenticator-App einrichten; Passkeys kommen dazu.
              </Notice>
            )}
            <Field label="Name" hint="Damit Sie ihn in der Liste wiedererkennen: „MacBook“, „iPhone“, „YubiKey“.">
              <Input
                required
                maxLength={64}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!status.enrolled}
              />
            </Field>
            <p className="text-xs muted">
              Nach dem Klick fragt der Browser, wo der Passkey liegen soll: auf
              diesem Gerät (Touch ID, Windows Hello), auf einem
              Sicherheitsschlüssel, oder auf dem Telefon — dafür zeigt er einen
              QR-Code, den Sie mit iPhone oder Android scannen.
            </p>
            {error && <ErrorNote>{error}</ErrorNote>}
            {added && (
              <Notice tone="success">
                „{added}“ ist registriert. Beim nächsten Anmelden reicht der Passkey.
              </Notice>
            )}
            <div>
              <Button
                type="submit"
                variant="primary"
                disabled={!status.enrolled || busy || name.trim() === ""}
              >
                <Fingerprint className="size-4" aria-hidden />
                {busy ? "warte auf den Passkey…" : "Passkey erzeugen"}
              </Button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Passkey entfernen"
        message={
          <>
            <strong>{removing?.name}</strong> kann sich danach nicht mehr
            anmelden. Der Eintrag auf dem Gerät oder im Passwortmanager bleibt
            bestehen und ist dann wertlos; er lässt sich dort löschen.
          </>
        }
        confirmLabel="Entfernen"
        error={remove.error ? (remove.error as Error).message : null}
        onConfirm={() => removing && remove.mutate(removing.id)}
        onClose={() => setRemoving(null)}
        busy={remove.isPending}
      />
    </Card>
  );
}

/** Eine Zeile der Liste; der Name lässt sich an Ort und Stelle ändern. */
function PasskeyRow({
  passkey,
  onRename,
  onRemove,
}: {
  passkey: Passkey;
  onRename: (name: string) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(passkey.name);

  const kind =
    passkey.attachment === "cross-platform"
      ? "Sicherheitsschlüssel"
      : passkey.attachment === "platform"
        ? "Gerät"
        : "—";

  return (
    <tr>
      <Td>
        {editing ? (
          <form
            className="flex items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim() && name.trim() !== passkey.name) onRename(name.trim());
              setEditing(false);
            }}
          >
            <Input
              autoFocus
              maxLength={64}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-7"
            />
            <Button type="submit" size="sm" variant="primary">
              OK
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setName(passkey.name);
                setEditing(false);
              }}
            >
              abbrechen
            </Button>
          </form>
        ) : (
          <span className="font-medium">{passkey.name}</span>
        )}
      </Td>
      <Td>
        <span className="inline-flex items-center gap-1">
          {kind}
          {passkey.backupState && <Badge tone="neutral">synchronisiert</Badge>}
        </span>
      </Td>
      <Td title={dateTime(passkey.createdAt)}>{relative(passkey.createdAt)}</Td>
      <Td title={passkey.lastUsedAt ? dateTime(passkey.lastUsedAt) : undefined}>
        {passkey.lastUsedAt ? relative(passkey.lastUsedAt) : "noch nie"}
      </Td>
      <Td className="text-right">
        {!editing && (
          <span className="inline-flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              umbenennen
            </Button>
            <Button size="sm" variant="ghost" onClick={onRemove}>
              entfernen
            </Button>
          </span>
        )}
      </Td>
    </tr>
  );
}
